import { useState } from "react";
import { useUsers } from "../hooks/useDirectory";
import { displayName } from "../api/tawa";
import { Avatar } from "../components/Avatar";
import { env } from "../lib/env";

type Tab = "membres" | "permissions" | "tenant";

/** Permission sets ATR Todo — lecture seule : « Brouillon · à déclarer (DT F-05) ».
 * La déclaration au registre ATS est une dette documentaire du DT ; aucune API
 * de gestion n'existe côté Modules (aucun rôle applicatif TM/PM). */
const PERMISSION_SETS = [
  {
    name: "worker-self",
    status: "Brouillon · à déclarer (DT F-05)",
    scopes: [
      "tasks:task:read",
      "tasks:task:write",
      "tasks:comment:write",
      "tasks:list:read",
      "tasks:list:write",
      "pm:project:read",
      "pm:work-item:read",
      "crm:party:read",
      "crm:relationship:read",
      "crm:role:read",
      "crm:graph:read",
    ],
  },
  {
    name: "manager-team",
    status: "Brouillon · à déclarer (DT F-05)",
    scopes: [
      "tasks:task:read",
      "tasks:task:write",
      "tasks:task:assign",
      "tasks:comment:write",
      "tasks:list:read",
      "pm:project:read",
      "pm:work-item:read",
      "crm:party:read",
    ],
  },
  {
    name: "admin-todo",
    status: "Brouillon · à déclarer (DT F-05)",
    scopes: ["tasks:*", "pm:project:read", "pm:work-item:read", "crm:*:read"],
  },
];

export function AdminScreen() {
  const [tab, setTab] = useState<Tab>("membres");
  const users = useUsers();

  return (
    <>
      <div
        className="view-header"
        style={{ display: "flex", alignItems: "baseline", gap: 16 }}
      >
        <div>
          <div className="view-title">Administration</div>
          <div className="view-caption">Configuration tenant Todo</div>
        </div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
          {(
            [
              ["membres", "Membres"],
              ["permissions", "Permission sets"],
              ["tenant", "Tenant"],
            ] as [Tab, string][]
          ).map(([k, label]) => (
            <button
              key={k}
              className="btn"
              style={
                tab === k
                  ? { background: "var(--bg-active)", fontWeight: 600 }
                  : undefined
              }
              onClick={() => setTab(k)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="main-scroll" style={{ maxWidth: 860 }}>
        {tab === "membres" && (
          <>
            <table className="data-table" style={{ marginTop: 16 }}>
              <thead>
                <tr>
                  <th>Utilisateur</th>
                  <th>Email</th>
                  <th>Rôle</th>
                  <th>Statut</th>
                </tr>
              </thead>
              <tbody>
                {(users.data ?? []).map((u) => (
                  <tr key={u.id}>
                    <td>
                      <span
                        style={{
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 8,
                        }}
                      >
                        <Avatar name={displayName(u)} size={22} />
                        <span style={{ fontWeight: 600 }}>
                          {displayName(u)}
                        </span>
                      </span>
                    </td>
                    <td>{u.email}</td>
                    <td>
                      <span className="tag">{u.tawa_role ?? "worker"}</span>
                    </td>
                    <td>
                      <span
                        className="chip"
                        style={
                          u.enabled
                            ? {
                                color: "var(--success)",
                                background: "var(--success-soft)",
                              }
                            : {
                                color: "var(--ink-muted)",
                                background: "var(--bg-chip)",
                              }
                        }
                      >
                        {u.enabled ? "Actif" : "Désactivé"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {users.isError && (
              <div className="empty-state">
                Annuaire indisponible (TAWA requis).
              </div>
            )}
            <p
              style={{ fontSize: 11, color: "var(--ink-ghost)", marginTop: 12 }}
            >
              La gestion des comptes (création, rôle, désactivation) se fait
              dans Chronos → Admin, annuaire partagé du tenant.
            </p>
          </>
        )}

        {tab === "permissions" && (
          <div style={{ marginTop: 16 }}>
            {PERMISSION_SETS.map((s) => (
              <div
                key={s.name}
                className="detail-section"
                style={{ marginTop: 10 }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span
                    className="mono"
                    style={{ fontWeight: 700, fontSize: 13 }}
                  >
                    {s.name}
                  </span>
                  <span
                    className="chip"
                    style={{
                      color: "var(--violet)",
                      background: "var(--violet-soft)",
                    }}
                  >
                    {s.status}
                  </span>
                </div>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 5,
                    marginTop: 8,
                  }}
                >
                  {s.scopes.map((sc) => (
                    <span key={sc} className="tag mono">
                      {sc}
                    </span>
                  ))}
                </div>
              </div>
            ))}
            <p
              style={{ fontSize: 11, color: "var(--ink-ghost)", marginTop: 12 }}
            >
              Permission sets ATR Todo — lecture seule. Déclaration au registre
              ATS avant qualification delivery (DT §15, dette documentaire).
            </p>
          </div>
        )}

        {tab === "tenant" && (
          <div className="detail-section" style={{ marginTop: 16 }}>
            <div className="detail-section-title">Configuration tenant</div>
            <div className="prop-row">
              <span className="prop-label">Tenant</span>
              <span className="prop-value mono">{env.VITE_TENANT_ID}</span>
            </div>
            <div className="prop-row">
              <span className="prop-label">Régime</span>
              <span className="prop-value">
                🟢 Online — API Central Connect, pas de flux WS/SSE (DT décision
                A5)
              </span>
            </div>
            <div className="prop-row">
              <span className="prop-label">Modules consommés</span>
              <span className="prop-value">
                <span className="tag">TM · écriture</span>{" "}
                <span className="tag">PM · lecture</span>{" "}
                <span className="tag">CRM · lecture</span>
              </span>
            </div>
            <div className="prop-row">
              <span className="prop-label">Realm</span>
              <span className="prop-value mono">{env.VITE_KEYCLOAK_REALM}</span>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
